import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import { createEnquiry } from "@/lib/enquiries";
import type { ServiceType } from "@/lib/services";

const schema = z.object({
  name: z.string().trim().min(2, "Enter your name").max(80, "Name is too long"),
  mobile: z
    .string()
    .trim()
    .min(7, "Enter a valid mobile number")
    .max(20, "Mobile number is too long")
    .regex(/^[0-9+\-\s()]+$/, "Mobile can only contain digits and + - ( )"),
  message: z.string().trim().min(5, "Add a short message").max(1000, "Message is too long"),
});

export interface EnquiryTarget {
  id: string;
  title: string;
  serviceType: ServiceType;
  listingKind: string;
  partnerId: string | null;
  demo?: boolean;
}

export function EnquiryForm({ target, onDone }: { target: EnquiryTarget; onDone: () => void }) {
  const { user, profile } = useAuth();
  const p = profile as { full_name?: string | null; phone?: string | null } | null;
  const [name, setName] = useState(p?.full_name ?? "");
  const [mobile, setMobile] = useState(p?.phone ?? "");
  const [message, setMessage] = useState(
    `Hi, I'm interested in "${target.title}". Please share more details.`,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const parsed = schema.safeParse({ name, mobile, message });
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] = issue.message;
      setErrors(next);
      return;
    }
    setErrors({});
    if (!user) return;
    setBusy(true);
    try {
      if (target.demo || !target.partnerId) {
        toast.success("Enquiry sent. The partner will respond shortly.");
        onDone();
        return;
      }
      await createEnquiry({
        serviceType: target.serviceType,
        listingId: target.id,
        listingKind: target.listingKind,
        listingTitle: target.title,
        partnerId: target.partnerId,
        customerId: user.id,
        name: parsed.data.name,
        mobile: parsed.data.mobile,
        message: parsed.data.message,
      });
      toast.success("Enquiry sent. The partner will respond shortly.");
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send the enquiry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label
        className="mt-3 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
        htmlFor="enq-name"
      >
        Name
      </label>
      <Input
        id="enq-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={80}
        className="mt-1"
      />
      {errors.name && <p className="mt-1 text-[11px] text-destructive">{errors.name}</p>}

      <label
        className="mt-3 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
        htmlFor="enq-mobile"
      >
        Mobile
      </label>
      <Input
        id="enq-mobile"
        inputMode="tel"
        value={mobile}
        onChange={(e) => setMobile(e.target.value)}
        maxLength={20}
        placeholder="+91 98765 43210"
        className="mt-1"
      />
      {errors.mobile && <p className="mt-1 text-[11px] text-destructive">{errors.mobile}</p>}

      <label
        className="mt-3 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
        htmlFor="enq-message"
      >
        Message
      </label>
      <Textarea
        id="enq-message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        maxLength={1000}
        className="mt-1"
      />
      {errors.message && <p className="mt-1 text-[11px] text-destructive">{errors.message}</p>}

      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onDone}
          className="rounded-xl border border-border py-2.5 text-sm font-semibold text-brown-dark transition hover:bg-secondary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="rounded-xl bg-brand py-2.5 text-sm font-bold text-brown-dark transition hover:brightness-95 disabled:opacity-60"
        >
          {busy ? "Sending…" : "Send enquiry"}
        </button>
      </div>
    </div>
  );
}
